import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { AlertShort, Loader, TextLink } from '@sdir/sds';
import { useNumberService } from '@sdir/utilities/lib/hooks';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import styled from 'styled-components';
import moment from 'moment';
import { SeagoingVesselPreviewLink } from '@sdir/blueprint';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import {
  SeagoingRow,
  SeagoingTableHeader,
  SeagoingChildTableHeader,
  SeagoingChildTableRow,
  TableDefinition,
  SeagoingChildTableHeaderMobile,
} from '../../../helpers/TableHeaders';
import { seagoingTimeCalculatorApi } from '../../../httpclient';
import { DisplayDateFormat } from '../../../helpers';
import { SortableDataTable } from '../../Molecules';
import { SeagoingLogInfo } from '../../Pages/SeagoingTimePage/SeagoingLogInfo';

const getOwnSeagoingTimeApiCall =
  seagoingTimeCalculatorApi?.v1SeagoingtimecalculatorGetownseagoingtimeQueryPost.bind(
    seagoingTimeCalculatorApi
  );

export const MySeagoing: React.FC = () => {
  const fm = useIntl().formatMessage;
  const numberService = useNumberService();
  const [showExpired, setShowExpired] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const { result, loading, errorObject, callApi } = useApi({
    apiFunction: getOwnSeagoingTimeApiCall,
  });

  const seagoingTable = useMemo<TableDefinition<SeagoingRow> | null>(() => {
    if (!result) return null;

    return {
      columns: SeagoingTableHeader(fm),
      data: result?.map<SeagoingRow>((x) => {
        const childTable: TableDefinition<SeagoingChildTableRow> = {
          columns: isMobile ? SeagoingChildTableHeaderMobile(fm) : SeagoingChildTableHeader(fm),
          data:
            x.details?.map((d) => {
              return {
                employer: d.organizationName || '',
                vessel: d.vessel ? (
                  <SeagoingVesselPreviewLink
                    vessel={d.vessel}
                    accumulatedData={d.accumulatedData || null}
                  />
                ) : null,
                vesselValue: d.vessel?.name || '',
                period: `${d.signOnDate ? DisplayDateFormat(d.signOnDate) : ''} - ${
                  d.signOffDate ? DisplayDateFormat(d.signOffDate) : ''
                }`,
                periodStart: moment(d.signOnDate),
                periodEnd: moment(d.signOffDate),
                days: numberService.formatDecimal(d.seagoingDays || 0),
                daysValue: d.seagoingDays || 0,
                details: <SeagoingLogInfo report={x} detail={d} />,
              };
            }) || [],
          sortableKeys: SeagoingChildTableHeader(fm)
            .map((c) => c.key)
            .filter((c) => c !== 'details'),
        };
        return {
          occupation: x.serviceDescription || '',
          days: numberService.formatDecimal(x.seagoingDays || 0),
          daysValue: x.seagoingDays || 0,
          childContent: <SortableDataTable className="child-table" table={childTable} />,
        };
      }),
      sortableKeys: '*',
      collapsable: true,
      collapsableText: [
        fm({ id: 'general.table.viewdetails' }),
        fm({ id: 'general.table.hidedetails' }),
      ],
    };
  }, [fm, numberService, result, isMobile]);

  const showEmpty = !result || result.length === 0;

  const toggleExpired = useCallback(() => setShowExpired(!showExpired), [showExpired]);

  useEffect(() => {
    callApi({
      take: 500,
      includeOutdatedReports: showExpired,
    });
  }, [callApi, showExpired]);

  useEffect(() => {
    function handleResize() {
      const isMobileScreen = !!(window?.innerWidth < 768);
      setIsMobile(isMobileScreen);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Wrapper>
      <h2>{fm({ id: 'seagoing.title' })}</h2>
      {loading && <Loader />}
      {errorObject && <ErrorMessage error={errorObject} />}
      {showEmpty && <AlertShort text={fm({ id: 'seagoing.notfound' })} type="info" />}
      {seagoingTable && !showEmpty && (
        <>
          <TableWrapper data-testid="seagoing-table">
            <SeagoingTable table={seagoingTable} />
          </TableWrapper>
          <TextLink
            text={
              showExpired
                ? fm({ id: 'seagoing.toggle.hide.five.year.old' })
                : fm({ id: 'seagoing.toggle.show.five.year.old' })
            }
            onClick={toggleExpired}
          />
        </>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
`;

const TableWrapper = styled.div`
  max-width: 100%;
  overflow: auto;

  & > div {
    margin-left: -5px;
    padding: 0 0 20px 0;
    background-color: transparent;

    thead tr th {
      padding-left: 0;
      button {
        text-align: left;
      }
    }

    tbody tr {
      border-top: 1px solid ${({ theme }) => theme.colors.primary8};
      min-height: 40px;

      &:last-child {
        border-bottom: 1px solid ${({ theme }) => theme.colors.primary8};
      }
    }

    & .child-table tr {
      border: none;
    }
  }
`;

const SeagoingTable = styled(SortableDataTable)`
  &&& {
    .open-row-content {
      padding: 0 2rem 0 0;
    }

    table > tbody > tr > td:last-child {
      text-align: right;
      > * {
        text-align: left;
      }

      .child-table {
        background: none;
        padding: 0 0 3rem 0;
      }
    }
  }
`;
